import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  Spacer,
  VStack,
  Text,
  Spinner,
  Center,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  FormControl,
  Select,
  Icon,
  SimpleGrid,
  useDisclosure,
} from "@chakra-ui/react";
import { SearchIcon, AddIcon } from "@chakra-ui/icons";
import { getModelById } from "../../services/DataModelService";
import { Header, ModelInfo } from "build-ts/metadata";
import { useConfigurationContext } from "context/ConfigurationContext";
import CustomModal from "../modals/CustomModal";
import EditMetricDefinitionContainer from "../../containers/configuration/EditMetricDefinitionContainer";
import { MdBarChart } from "react-icons/md";
import ListItemMeta from "./ListItemMeta";
import ConfigurationListFilterControl from "./ConfigurationListFilterControl";
import { listPanelScrollSx } from "utils/listScrollAreaStyles";
import { useMatch } from "react-router-dom";

interface MetricListComponentProps {
  onReloadKnowledgeGraph?: () => void;
  /** When the split detail panel is open, use the narrow single-column list; otherwise a 3-column pill grid. */
  splitDetailOpen: boolean;
}

const MetricListComponent: React.FC<MetricListComponentProps> = ({
  onReloadKnowledgeGraph,
  splitDetailOpen,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const createMetricModal = useDisclosure();
  const {
    fetchAllMetrics,
    metricList,
    modelList,
    selectedMetric,
    setSelectedMetric,
    setSelectedBenchmark,
    setSelectedKgColumn,
    handleEditMetricDefinitionModal,
  } = useConfigurationContext();
  const [createMetricFlag, setCreateMetricFlag] = useState<boolean>(false);
  const [modelInfoData, setModelInfoData] = useState<ModelInfo | null>(null);
  const [selectedModelId, setSelectedModelId] = useState<string>("");
  const [openCreateMetricForm, setOpenCreateMetricForm] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filteredMetrics, setFilteredMetrics] = useState<Header[]>([]);
  const metricsIdMatch = useMatch("/mkg/metrics/:id");
  const duplicateReloadRef = useRef<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadMetrics = async () => {
      setIsLoading(true);
      try {
        if (!metricList || metricList.length === 0) {
          await fetchAllMetrics();
        }
      } catch (error) {
        console.error("Error fetching metrics:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadMetrics();
  }, []);

  // auto-select a metric when the URL contains /mkg/metrics/:id
  useEffect(() => {
    const id = metricsIdMatch?.params?.id;
    if (!id || !metricList.length) return;
    if (duplicateReloadRef.current === id) return;
    const header = metricList.find(h => h.id === id);
    if (!header) return;
    handleEditMetricDefinitionModal(header);
    duplicateReloadRef.current = id;
  }, [metricList, metricsIdMatch?.params?.id]);

  const handleMetricClick = (metric: Header) => {
    handleEditMetricDefinitionModal(metric);
    setSelectedBenchmark(null);
    setSelectedKgColumn(null);
    navigate(`/mkg/metrics/${metric.id}`);
  };

  const hasActiveFilters = searchQuery.trim() !== "" || filteredMetrics.length < metricList.length;

  const closeCreateMetric = () => {
    setCreateMetricFlag(false);
    setOpenCreateMetricForm(false);
    setSelectedModelId("");
    createMetricModal.onClose();
  };

  const handleCreateMetric = async (id: string) => {
    if (id === "") {
      return;
    }
    const data = await getModelById(id);
    if (data) {
      setModelInfoData(data);
    }
    setOpenCreateMetricForm(true);
  };

  const openCreateMetricModal = () => {
    setSelectedMetric(null as any);
    setCreateMetricFlag(true);
    createMetricModal.onOpen();
  };

  return (
    <>
      <Box h="100%" minH={0} display="flex" flexDirection="column">
        {/* Search and actions bar */}
        <Box px={3} py={3} flexShrink={0}>
          <HStack spacing={3} alignItems="center" width="100%">
            <InputGroup flex="1" maxW="400px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search metrics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                bg="white"
                size="sm"
                _focus={{
                  borderColor: "purple.700",
                  boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)"
                }}
              />
            </InputGroup>

            <Button
              onClick={openCreateMetricModal}
              leftIcon={<AddIcon boxSize={3} />}
              size="xs"
              aria-label="Create New Plan"
              borderRadius="md"
              title="Create New Plan"
              bg="gray.50"
              color="purple.700"
              _hover={{ bg: "purple.700", color: "white" }}
              variant="outline"
              borderWidth="2px"
              borderColor="purple.700"
              fontWeight="700"
              iconSpacing={1}
              cursor="pointer"
            >
              New
            </Button>
          </HStack>
        </Box>

        {/* Metrics List */}
        <Box
          flex="1"
          overflowY="auto"
          overflowX="hidden"
          pl={4}
          pr={splitDetailOpen ? 6 : 4}
          pb={14}
          minHeight={0}
          sx={listPanelScrollSx(splitDetailOpen)}
        >
          {isLoading ? (
            <Center height="100%">
              <Spinner size="md" color="purple.500" />
            </Center>
          ) : (
            <VStack spacing={2} align="stretch">
              {/* Section title: tab icon + label (main list only; expanded sidebar omits duplicate icons) */}
              <HStack justify="space-between" align="center" mx={1} p={0} w="full" minW={0} spacing={2}>
                <HStack spacing={2} align="center" flexShrink={0} minW={0}>
                  {!splitDetailOpen && (
                    <Icon as={MdBarChart} boxSize={7} color="gray.700" flexShrink={0} />
                  )}
                  <Text
                    fontSize="sm"
                    fontWeight="700"
                    color="gray.700"
                    letterSpacing="tight"
                    mb={0}
                    noOfLines={splitDetailOpen ? 1 : undefined}
                  >
                    Metrics
                  </Text>
                </HStack>
                {!splitDetailOpen && <Box flex="1" minW={2} height="1px" bg="gray.300" mx={1} />}
                <ConfigurationListFilterControl
                  headerList={metricList}
                  type="metric"
                  modelList={modelList}
                  searchQuery={searchQuery}
                  splitDetailOpen={splitDetailOpen}
                  onChange={setFilteredMetrics}
                  defaultSortOption="modified_desc"
                />
              </HStack>
              {filteredMetrics.length > 0 ? (
                splitDetailOpen ? (
                  filteredMetrics.map((metric) => {
                    const sel = selectedMetric?.id === metric.id;
                    return (
                      <Button
                        key={metric.id}
                        variant="outline"
                        size="sm"
                        flexDirection="column"
                        alignItems="stretch"
                        justifyContent="flex-start"
                        borderColor={sel ? "purple.600" : "gray.200"}
                        _hover={{
                          bg: sel ? "purple.700" : "purple.50",
                          borderColor: "purple.400",
                          color: sel ? "white" : "gray.800",
                        }}
                        bg={sel ? "purple.700" : "white"}
                        color={sel ? "white" : "gray.800"}
                        borderRadius="lg"
                        fontSize="sm"
                        fontWeight={sel ? "600" : "400"}
                        height="auto"
                        minH="4.5rem"
                        py={3}
                        px={4}
                        whiteSpace="normal"
                        textAlign="left"
                        onClick={() => handleMetricClick(metric)}
                      >
                        <Flex direction="column" align="stretch" flex={1} w="full" minH="full" minW={0}>
                          <HStack align="flex-start" spacing={2} w="full" minW={0} pt={0.5}>
                            <Icon
                              as={MdBarChart}
                              boxSize={5}
                              flexShrink={0}
                              color={sel ? "whiteAlpha.900" : "purple.600"}
                              mt={0.5}
                              aria-hidden
                            />
                            <Text mb={0} fontWeight={sel ? "600" : "500"} noOfLines={2} fontSize="md" textAlign="left" flex={1} minW={0}>
                              {metric.name}
                            </Text>
                          </HStack>
                          <Spacer minH={2} />
                          <Box w="full" pt={1}>
                            <ListItemMeta header={metric} selected={sel} />
                          </Box>
                        </Flex>
                      </Button>
                    );
                  })
                ) : (
                  <SimpleGrid columns={3} spacing={3} w="full">
                    {filteredMetrics.map((metric) => {
                      const selected = selectedMetric?.id === metric.id;
                      return (
                        <Button
                          key={metric.id}
                          w="full"
                          h="auto"
                          minH="6rem"
                          py={4}
                          px={5}
                          borderRadius="2xl"
                          variant="outline"
                          borderWidth={selected ? "2px" : "1px"}
                          borderColor={selected ? "purple.600" : "gray.200"}
                          flexDirection="column"
                          alignItems="stretch"
                          justifyContent="flex-start"
                          bg={selected ? "purple.700" : "white"}
                          color={selected ? "white" : "gray.800"}
                          _hover={{
                            bg: selected ? "purple.700" : "purple.50",
                            borderColor: "purple.400",
                          }}
                          onClick={() => handleMetricClick(metric)}
                        >
                          <Flex direction="column" align="stretch" flex={1} w="full" minH="full" minW={0}>
                            <HStack align="flex-start" spacing={2} w="full" minW={0} pt={1}>
                              <Icon
                                as={MdBarChart}
                                boxSize={6}
                                flexShrink={0}
                                color={selected ? "whiteAlpha.900" : "purple.600"}
                                aria-hidden
                              />
                              <Text
                                fontWeight="600"
                                fontSize="md"
                                noOfLines={2}
                                lineHeight="short"
                                textAlign="left"
                                mb={0}
                                flex={1}
                                minW={0}
                              >
                                {metric.name}
                              </Text>
                            </HStack>
                            <Spacer minH={2} />
                            <Box w="full" pt={1}>
                              <ListItemMeta header={metric} selected={selected} />
                            </Box>
                          </Flex>
                        </Button>
                      );
                    })}
                  </SimpleGrid>
                )
              ) : (
                <Center py={8}>
                  <Text color="gray.500" textAlign="center" px={2}>
                    {hasActiveFilters
                      ? "No metrics match your search or filters."
                      : "No metrics found."}
                  </Text>
                </Center>
              )}
            </VStack>
          )}
        </Box>
      </Box>

      {createMetricFlag && (
        <CustomModal
          isOpen={createMetricModal.isOpen}
          onClose={closeCreateMetric}
          title={
            <>
              {modelInfoData ?
                <Text fontWeight="bold" display="inline" color="purple.700">{modelInfoData?.header?.name} </Text>
                :
                <Text fontWeight="bold" display="inline" color="purple.700">Select Model </Text>
              }
            </>
          }
          maxWidth="50vw"
          maxHeight="90vh"
          isCentered={true}
        >
          <VStack>
            <FormControl maxW="md">
              <HStack pb="15px">
                <Select
                  placeholder="Select a model"
                  value={selectedModelId}
                  onChange={(e) => {
                    setSelectedModelId(e.target.value);
                    handleCreateMetric(e.target.value);
                  }}
                >
                  {modelList.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name}
                    </option>
                  ))}
                </Select>

              </HStack>
            </FormControl>
            {openCreateMetricForm && (
              <EditMetricDefinitionContainer
                createMetricFlag={true}
                modelInfo={modelInfoData}
                metricHeader={null}
                onClose={closeCreateMetric}
                onReloadKnowledgeGraph={onReloadKnowledgeGraph}
              />
            )}
          </VStack>
        </CustomModal>
      )}

    </>
  );
};

export default MetricListComponent;
